import type { ReactNode } from 'react';
import { Logo } from '../layout/Logo.js';
import '../../pages/auth-page.css';

export interface OnboardingStepMeta {
  /** Stable id used internally — not user-visible. */
  id: string;
  /** Short label shown in the progress strip ("Brand", "Team", …). */
  shortLabel: string;
}

interface OnboardingShellProps {
  steps: ReadonlyArray<OnboardingStepMeta>;
  currentStepId: string;
  illustration: ReactNode;
  /** Panel copy under the preview. Falls back to the generic wizard line. */
  headline?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}

/**
 * Two-column wizard layout, sharing the auth screens' shell
 * (see pages/auth-page.css) so signup → verify → onboarding reads as one
 * continuous flow. Left panel = drifting gradient with the live proptree
 * preview floating on it; right column = the active step's form.
 */
export function OnboardingShell({
  steps,
  currentStepId,
  illustration,
  headline,
  description,
  children,
}: OnboardingShellProps) {
  const idx = steps.findIndex((s) => s.id === currentStepId);
  return (
    <main className="auth-shell">
      <section className="auth-panel auth-panel--wizard" aria-label="Onboarding preview">
        <div className="auth-field" aria-hidden="true" />
        <div className="auth-orb auth-orb-light" aria-hidden="true" />
        <div className="auth-orb auth-orb-deep" aria-hidden="true" />
        <div className="auth-bloom" aria-hidden="true" />
        <div className="auth-grain" aria-hidden="true" />

        <div className="auth-panel-meta">
          <div className="auth-brand">
            <Logo size="md" />
          </div>
          <p className="auth-eyebrow">
            {steps.length > 1 && idx >= 0
              ? `Step ${idx + 1} of ${steps.length}`
              : 'Onboarding'}
          </p>
        </div>

        {/* Re-keyed on currentStepId so the per-step illustration re-mounts
            and animates in cleanly when the wizard advances. */}
        <div
          key={`illu-${currentStepId}`}
          className="auth-wizard-stage animate-in fade-in slide-in-from-bottom-3 duration-700 ease-out"
        >
          {illustration}
        </div>

        <div className="auth-panel-copy">
          <h2>
            {headline ?? (
              <>
                Your proptree.
                <br />
                <em>Live in minutes.</em>
              </>
            )}
          </h2>
          <p className="auth-panel-description">
            {description ?? (
              <>
                <strong>A preview seeded with example listings</strong> — the
                logo and details come straight from your website, and can be
                updated later.
              </>
            )}
          </p>
        </div>
      </section>

      <section className="auth-form-column">
        {/* Re-keyed on currentStepId so the per-step body re-mounts and fades
            in alongside the illustration on step swap. */}
        <div
          key={`body-${currentStepId}`}
          className="auth-form auth-form--wizard animate-in fade-in slide-in-from-bottom-3 duration-700 ease-out"
        >
          {children}
        </div>

        <nav className="auth-legal" aria-label="Legal and support">
          <a href="/privacy">Privacy &amp; terms</a>
          <a href="/contact">Contact us</a>
          <a href="/support">Support</a>
        </nav>
      </section>
    </main>
  );
}
